import { useEffect, useRef, useState } from 'react';
import { supabase } from '../utils/supabaseClient';
import { setSavedVideoDeviceId, setSavedAudioDeviceId, setSavedAudioOutputDeviceId } from '../utils/deviceStorage';
import { useMediaDevices } from './useMediaDevices';
import { getIceServers } from '../utils/webrtcIceServers';

const SIZES = [140, 200];
const MODE_COUNT = 3; // 0=small, 1=medium, 2=focus/blur

/**
 * Owns the whole WebRTC/signaling lifecycle for a lesson call: getUserMedia,
 * RTCPeerConnection setup, Supabase Realtime broadcast/presence signaling,
 * device switching, and the small bits of UI state (mute, size mode, error
 * banners) the two VideoCallModal render modes need. VideoCallModal itself
 * only renders the two floating-panel layouts against what this returns.
 */
export function useVideoCall({
  isOpen, onClose, lessonId,
  isStudent = false, autoJoin = false, autoStart = false,
  initialVideoDeviceId = null, initialAudioDeviceId = null, initialAudioOutputDeviceId = null,
}) {
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('idle');
  const [mediaReady, setMediaReady] = useState(false);
  const [channelReady, setChannelReady] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [currentVideoDeviceId, setCurrentVideoDeviceId] = useState(initialVideoDeviceId);
  const [currentAudioDeviceId, setCurrentAudioDeviceId] = useState(initialAudioDeviceId);
  const [currentAudioOutputDeviceId, setCurrentAudioOutputDeviceId] = useState(initialAudioOutputDeviceId);
  const [sizeIdx, setSizeIdx] = useState(0);
  const [iceState, setIceState] = useState('new');
  const [showRelayBtn, setShowRelayBtn] = useState(false);
  // Gates the actual hangUp() behind a "Leave meeting?"-style confirmation,
  // for both roles — either side leaving disconnects the other side's
  // active peer connection too, so it's worth a confirm click.
  const [confirmEndOpen, setConfirmEndOpen] = useState(false);
  // Chrome/Edge only — Firefox/Safari don't implement setSinkId(), so the
  // speaker section of the mic device menu is hidden there instead of
  // showing a picker that would silently do nothing.
  const [speakerSupported] = useState(() => typeof document.createElement('video').setSinkId === 'function');
  const { cameras, microphones, speakers, refresh: refreshDevices } = useMediaDevices();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const pcPollTimerRef = useRef(null);
  const presencePollTimerRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const callChannelRef = useRef(null);
  const originalAudioTrackRef = useRef(null);
  const originalVideoTrackRef = useRef(null);
  // RTCRtpSender for each track kind, captured when addTrack() runs, so
  // switchCamera/switchMic can later call sender.replaceTrack() without a
  // renegotiation (no new offer/answer round-trip needed for a device swap).
  const audioSenderRef = useRef(null);
  const videoSenderRef = useRef(null);
  const audioOutputDeviceIdRef = useRef(initialAudioOutputDeviceId);
  // pendingCandidatesRef buffers candidates that arrive before
  // setRemoteDescription has run (can't call addIceCandidate before that).
  const pendingCandidatesRef = useRef([]);
  // callId tags every offer/answer/candidate for one connection attempt so
  // a stray message from an unrelated/earlier attempt can't get applied to
  // the current RTCPeerConnection.
  const callIdRef = useRef(null);

  const statusRef = useRef('idle');
  const mediaReadyRef = useRef(false);
  const iceStateRef = useRef('new');
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { mediaReadyRef.current = mediaReady; }, [mediaReady]);

  // Device labels are blank until getUserMedia has granted permission —
  // re-enumerate once that's happened so the switch-device menus show
  // real names instead of "Device 1" placeholders.
  useEffect(() => {
    if (mediaReady) refreshDevices();
  }, [mediaReady, refreshDevices]);
  useEffect(() => { iceStateRef.current = iceState; }, [iceState]);

  // Add/remove body class so CSS can hide overlapping editor UI in focus mode
  useEffect(() => {
    if (sizeIdx === 2) {
      document.body.classList.add('vc-focus-mode');
    } else {
      document.body.classList.remove('vc-focus-mode');
    }
    return () => document.body.classList.remove('vc-focus-mode');
  }, [sizeIdx]);

  // Pre-warm TURN credentials cache as soon as modal opens
  useEffect(() => {
    if (isOpen) getIceServers();
  }, [isOpen]);

  // Some VPNs let TURN credentials through but block the actual UDP/TCP
  // relay traffic on the default candidate pair. Depending on the browser,
  // that shows up either as 'disconnected'/'failed', or — seen on mobile —
  // as ICE just sitting in 'checking' forever without ever escalating.
  // Offer a manual "Force relay" retry rather than nothing, but wait long
  // enough that we don't flash it on a first negotiation that would have
  // succeeded on its own.
  useEffect(() => {
    if (iceState === 'failed') { setShowRelayBtn(true); return; }
    if (iceState !== 'disconnected' && iceState !== 'checking') { setShowRelayBtn(false); return; }
    const delay = iceState === 'checking' ? 18000 : 5000;
    const t = setTimeout(() => setShowRelayBtn(true), delay);
    return () => clearTimeout(t);
  }, [iceState]);

  // Re-apply srcObject after mode change (conditional rendering remounts <video> elements)
  useEffect(() => {
    if (localVideoRef.current && localStreamRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
    }
    if (remoteVideoRef.current && remoteStreamRef.current) {
      remoteVideoRef.current.srcObject = remoteStreamRef.current;
      applySinkId(remoteVideoRef.current);
    }
  }, [sizeIdx]);

  useEffect(() => {
    if (!channelReady || !mediaReady || statusRef.current !== 'idle') return;
    if (!isStudent && autoStart) startCall();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelReady, mediaReady]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    (async () => {
      const constraints = {
        video: initialVideoDeviceId ? { deviceId: { exact: initialVideoDeviceId } } : true,
        audio: initialAudioDeviceId ? { deviceId: { exact: initialAudioDeviceId } } : true,
      };
      try {
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (e) {
          // Chosen device may have disappeared between the pre-join screen
          // and now (unplugged, permission revoked) — fall back to defaults
          // rather than failing the whole call.
          if (e.name === 'OverconstrainedError' || e.name === 'NotFoundError') {
            stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          } else {
            throw e;
          }
        }
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        stream.getTracks().forEach(t => { t.onended = () => handleDeviceUnplugged(t.kind); });
        localStreamRef.current = stream;
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
        setMediaReady(true);
      } catch {
        if (!cancelled) setError('Camera/mic access denied');
      }
    })();

    const setupChannel = () => {
      const ch = supabase.channel(`lesson-call-${lessonId}`, {
        config: {
          broadcast: { self: false, ack: false },
          presence: { key: `${isStudent ? 'student' : 'teacher'}-${lessonId}` },
        },
      });
      callChannelRef.current = ch;

      ch.on('broadcast', { event: 'vc-answer' }, async ({ payload }) => {
        if (isStudent) return;
        console.log('[WebRTC] vc-answer received, callId match:', payload?.callId === callIdRef.current);
        const pc = pcRef.current;
        if (!pc || pc.currentRemoteDescription) return;
        if (payload.callId !== callIdRef.current) return;
        try {
          await pc.setRemoteDescription(new RTCSessionDescription(payload.answer));
          console.log('[WebRTC] vc-answer applied — remote description set');
          flushPendingCandidates(pc);
          setStatus('in_call');
        } catch (e) {
          console.error('[WebRTC] Failed to apply answer:', e);
          setError('Connection failed: ' + (e.message || ''));
        }
      });

      ch.on('broadcast', { event: 'vc-ice-candidate' }, ({ payload }) => {
        const pc = pcRef.current;
        const callIdMatches = payload?.callId === callIdRef.current;
        console.log('[WebRTC] remote ICE candidate received, callId match:', callIdMatches, 'have pc:', !!pc, 'theirCallId:', payload?.callId, 'ourCallId:', callIdRef.current);
        if (!pc || !payload || !callIdMatches) return;
        if (!pc.remoteDescription) {
          pendingCandidatesRef.current.push(payload.candidate);
          return;
        }
        pc.addIceCandidate(new RTCIceCandidate(payload.candidate))
          .then(() => console.log('[WebRTC] remote ICE candidate applied'))
          .catch(e => console.error('[WebRTC] remote ICE candidate failed to apply:', e));
      });

      ch.on('broadcast', { event: 'vc-hangup' }, () => {
        doCleanup(false);
      });

      // Sync size/focus mode from the other party
      ch.on('broadcast', { event: 'vc-sizemode' }, ({ payload }) => {
        if (typeof payload?.sizeIdx === 'number') setSizeIdx(payload.sizeIdx);
      });

      // Given an offer we've learned about (from presence OR a direct
      // broadcast), join it if idle, or — if the teacher recreated their
      // offer (reconnect, Force relay retry) while we're not connected
      // yet — rejoin with the new one instead of staying stuck on a stale
      // callId that no incoming candidate will ever match again.
      const maybeFollowOffer = (offer) => {
        if (!isStudent || !autoJoin || !mediaReadyRef.current || !offer) return;

        if (statusRef.current === 'idle') {
          joinCallWithOffer(offer);
          return;
        }
        const connected = iceStateRef.current === 'connected' || iceStateRef.current === 'completed';
        if (!connected && offer.callId !== callIdRef.current) {
          console.log('[WebRTC] Teacher offer changed while not connected — rejoining with new offer');
          statusRef.current = 'idle';
          setStatus('idle');
          joinCallWithOffer(offer);
        }
      };

      // Direct broadcast of the offer — more reliable than presence
      // track(), which was observed to silently fail to propagate on a
      // flaky connection in testing.
      ch.on('broadcast', { event: 'vc-offer' }, ({ payload }) => {
        console.log('[WebRTC] vc-offer broadcast received, callId:', payload?.offer?.callId);
        maybeFollowOffer(payload?.offer);
      });

      // Re-tracking presence with a new offer may surface to other clients
      // as either 'sync' or 'join' depending on the Realtime client
      // version — handle both, as a fallback alongside the broadcast above.
      const handlePresenceUpdate = () => {
        if (!isStudent || !autoJoin || !mediaReadyRef.current) return;
        const state = ch.presenceState();
        const teacherEntry = Object.values(state).flat()
          .find(e => e.role === 'teacher' && e.hasOffer && e.offer);
        if (!teacherEntry) return;
        maybeFollowOffer(teacherEntry.offer);
      };
      ch.on('presence', { event: 'sync' }, handlePresenceUpdate);
      ch.on('presence', { event: 'join' }, handlePresenceUpdate);

      // Belt and suspenders again: don't trust presence event delivery
      // alone (we've now seen ICE/connection change events go silently
      // missing on this same codebase) — poll presence state directly
      // until actually connected.
      presencePollTimerRef.current = setInterval(() => {
        const connected = iceStateRef.current === 'connected' || iceStateRef.current === 'completed';
        if (connected) {
          clearInterval(presencePollTimerRef.current);
          presencePollTimerRef.current = null;
          return;
        }
        handlePresenceUpdate();
      }, 2000);

      ch.subscribe(async (s) => {
        if (cancelled) return;

        if (s === 'SUBSCRIBED') {
          await ch.track({ role: isStudent ? 'student' : 'teacher', hasOffer: false }).catch(() => {});
          setChannelReady(true);

          if (isStudent && autoJoin && mediaReadyRef.current && statusRef.current === 'idle') {
            const state = ch.presenceState();
            const teacherEntry = Object.values(state).flat()
              .find(e => e.role === 'teacher' && e.hasOffer && e.offer);
            if (teacherEntry) joinCallWithOffer(teacherEntry.offer);
          }
          return;
        }

        // TIMED_OUT / CLOSED / CHANNEL_ERROR: the underlying Realtime
        // websocket dropped (VPN toggle, wifi/cell handoff, etc). Just
        // reflect that in the UI — supabase-js reconnects the socket and
        // rejoins this SAME channel on its own with exponential backoff,
        // which re-invokes this exact callback with 'SUBSCRIBED' once it's
        // back (handled above). Manually tearing down and recreating the
        // channel here raced with that built-in recovery (removeChannel()
        // is async and unsubscribing an already-dead channel doesn't
        // reliably resolve), leaving a stale, already-subscribed channel
        // object that .channel(sameTopic) kept returning — every retry
        // then threw trying to add presence callbacks to it, which is what
        // caused the "Maximum call stack size exceeded" crash.
        if (s === 'TIMED_OUT' || s === 'CLOSED' || s === 'CHANNEL_ERROR') {
          setChannelReady(false);
        }
      });
    };
    setupChannel();

    return () => {
      cancelled = true;
      doCleanup(true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lessonId]);

  const doCleanup = (stopMedia = true) => {
    if (pcPollTimerRef.current) {
      clearInterval(pcPollTimerRef.current);
      pcPollTimerRef.current = null;
    }
    if (presencePollTimerRef.current) {
      clearInterval(presencePollTimerRef.current);
      presencePollTimerRef.current = null;
    }
    if (pcRef.current) {
      try { pcRef.current.close(); } catch (_) {}
      pcRef.current = null;
    }
    const ch = callChannelRef.current;
    if (ch) {
      try { ch.untrack(); } catch (_) {}
      try { supabase.removeChannel(ch); } catch (_) {}
      callChannelRef.current = null;
    }
    if (stopMedia && localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    originalAudioTrackRef.current = null;
    originalVideoTrackRef.current = null;
    setStatus('idle');
    setIceState('new');
    setError(null);
    setShowRelayBtn(false);
    setMediaReady(false);
    setChannelReady(false);
  };

  // setSinkId (choosing which speaker plays remote audio) is Chrome/Edge
  // only — Safari and Firefox don't implement it, so this is a no-op there
  // and the speaker-picker UI is hidden for those browsers instead.
  const applySinkId = (videoEl) => {
    const deviceId = audioOutputDeviceIdRef.current;
    if (!deviceId || typeof videoEl.setSinkId !== 'function') return;
    videoEl.setSinkId(deviceId).catch(e => console.warn('[WebRTC] setSinkId failed:', e.message));
  };

  const flushPendingCandidates = (pc) => {
    const pending = pendingCandidatesRef.current;
    pendingCandidatesRef.current = [];
    console.log('[WebRTC] flushing buffered remote candidates:', pending.length);
    pending.forEach(c => { pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {}); });
  };

  const ensurePc = async (sharedCallId = null, forceRelay = false) => {
    if (pcRef.current && !forceRelay) return pcRef.current;
    if (pcPollTimerRef.current) {
      clearInterval(pcPollTimerRef.current);
      pcPollTimerRef.current = null;
    }
    if (pcRef.current) {
      try { pcRef.current.close(); } catch (_) {}
      pcRef.current = null;
    }
    pendingCandidatesRef.current = [];
    const callId = sharedCallId || ((typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
    callIdRef.current = callId;

    const iceServers = await getIceServers();
    const config = { iceServers, iceCandidatePoolSize: 8 };
    if (forceRelay) config.iceTransportPolicy = 'relay';
    const pc = new RTCPeerConnection(config);
    pc._callId = callId;
    pcRef.current = pc;

    remoteStreamRef.current = new MediaStream();
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStreamRef.current;

    const ls = localStreamRef.current;
    if (ls) {
      ls.getTracks().forEach(track => {
        const sender = pc.addTrack(track, ls);
        if (track.kind === 'audio') {
          if (!originalAudioTrackRef.current) originalAudioTrackRef.current = track;
          audioSenderRef.current = sender;
        }
        if (track.kind === 'video') {
          if (!originalVideoTrackRef.current) originalVideoTrackRef.current = track;
          videoSenderRef.current = sender;
        }
      });
    }

    pc.ontrack = ({ track, streams }) => {
      const remote = remoteStreamRef.current;
      if (streams && streams[0]) {
        streams[0].getTracks().forEach(t => { if (!remote.getTracks().includes(t)) remote.addTrack(t); });
      } else {
        if (!remote.getTracks().includes(track)) remote.addTrack(track);
      }
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remote;
        applySinkId(remoteVideoRef.current);
      }
    };

    // Trickle ICE: send each candidate over signaling as soon as it's
    // found instead of waiting for gathering to finish.
    pc.onicecandidate = ({ candidate }) => {
      if (!candidate) return;
      console.log('[WebRTC] ICE candidate:', candidate.type, candidate.protocol || '', candidate.address || '');
      const ch = callChannelRef.current;
      if (ch) {
        ch.send({
          type: 'broadcast',
          event: 'vc-ice-candidate',
          payload: { candidate: candidate.toJSON(), callId },
        }).catch(() => {});
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('[WebRTC] ICE connection state:', pc.iceConnectionState);
      setIceState(pc.iceConnectionState);
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        setError(null);
      }
    };

    // Safari/WebKit doesn't reliably fire oniceconnectionstatechange — fall
    // back to the newer connectionState API, which it does fire.
    pc.onconnectionstatechange = () => {
      console.log('[WebRTC] Connection state:', pc.connectionState);
      const mapped = pc.connectionState === 'connecting' ? 'checking' : pc.connectionState;
      setIceState(mapped);
      if (pc.connectionState === 'connected') setError(null);
    };

    // Belt and suspenders: on some mobile browsers neither change event
    // above fires reliably even though the state genuinely changes, so poll
    // the real state directly instead of trusting only event delivery.
    let lastPolled = null;
    const pollTimer = setInterval(() => {
      if (pc.signalingState === 'closed') { clearInterval(pollTimer); return; }
      const s = pc.iceConnectionState;
      if (s !== lastPolled) {
        lastPolled = s;
        console.log('[WebRTC] (poll) ICE connection state:', s);
        setIceState(s);
        if (s === 'connected' || s === 'completed') setError(null);
      }
    }, 1000);
    pcPollTimerRef.current = pollTimer;

    return pc;
  };

  // Trickle ICE sends each candidate as its own Realtime broadcast message —
  // fine on a stable connection, but on a flaky one (VPN dropouts we've
  // observed mid-test) losing even one of a dozen+ messages breaks the
  // whole exchange. Wait for gathering to finish (capped) so the initial
  // offer/answer SDP already carries every candidate found so far as a
  // single message — trickle candidates found after that (or arriving late)
  // still get sent separately as a bonus, but the connection no longer
  // depends on all of them getting through.
  const waitForIceGathering = (pc, timeoutMs = 8000) => new Promise(resolve => {
    if (pc.iceGatheringState === 'complete') { resolve(); return; }
    const check = () => {
      if (pc.iceGatheringState === 'complete') {
        pc.removeEventListener('icegatheringstatechange', check);
        clearTimeout(timer);
        resolve();
      }
    };
    const timer = setTimeout(() => {
      pc.removeEventListener('icegatheringstatechange', check);
      resolve();
    }, timeoutMs);
    pc.addEventListener('icegatheringstatechange', check);
  });

  const startCall = async (forceRelay = false) => {
    if (statusRef.current !== 'idle') return;
    statusRef.current = 'starting';
    setError(null);
    setStatus('starting');
    try {
      const ch = callChannelRef.current;
      if (!ch) throw new Error('Channel not ready');
      const pc = await ensurePc(null, forceRelay);

      const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
      await pc.setLocalDescription(offer);
      await waitForIceGathering(pc);

      const { type, sdp } = pc.localDescription;
      if (!type || !sdp) return;
      const offerPayload = { type, sdp, callId: pc._callId };
      await ch.track({ role: 'teacher', hasOffer: true, offer: offerPayload }).catch(e => {
        console.error('[WebRTC] presence track (offer) failed:', e);
      });
      // Presence track() updates have proven unreliable across a flaky
      // connection (silently lost on at least one real test) — also
      // broadcast the offer directly over the same channel that ICE
      // candidates use, which has been reliable throughout.
      ch.send({ type: 'broadcast', event: 'vc-offer', payload: { offer: offerPayload } }).catch(() => {});

      setStatus('starting');
    } catch (e) {
      setError(e.message || 'Start failed');
      setStatus('idle');
    }
  };

  const joinCallWithOffer = async (offerDesc, forceRelay = false) => {
    if (statusRef.current !== 'idle') return;
    statusRef.current = 'joining';
    setError(null);
    setStatus('joining');
    try {
      const ch = callChannelRef.current;
      if (!ch) throw new Error('Channel not ready');
      const pc = await ensurePc(offerDesc.callId, forceRelay);

      await pc.setRemoteDescription(new RTCSessionDescription(offerDesc));
      flushPendingCandidates(pc);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await waitForIceGathering(pc);

      const { type, sdp } = pc.localDescription;
      if (!type || !sdp) return;
      await ch.send({ type: 'broadcast', event: 'vc-answer', payload: { answer: { type, sdp }, callId: pc._callId } });

      setStatus('in_call');
    } catch (e) {
      setError(e.message || 'Join failed');
      setStatus('idle');
    }
  };

  const joinCall = async () => {
    const ch = callChannelRef.current;
    if (!ch) { setError('Channel not ready'); return; }
    const state = ch.presenceState();
    const teacherEntry = Object.values(state).flat()
      .find(e => e.role === 'teacher' && e.hasOffer && e.offer);
    if (!teacherEntry) { setError('No active call to join'); return; }
    await joinCallWithOffer(teacherEntry.offer);
  };

  const retryRelay = async () => {
    setShowRelayBtn(false);
    setError(null);
    setIceState('new');
    statusRef.current = 'idle';
    setStatus('idle');
    if (!isStudent) {
      await startCall(true);
    } else {
      const ch = callChannelRef.current;
      if (!ch) return;
      const state = ch.presenceState();
      const teacherEntry = Object.values(state).flat()
        .find(e => e.role === 'teacher' && e.hasOffer && e.offer);
      if (teacherEntry) {
        await joinCallWithOffer(teacherEntry.offer, true);
      } else {
        setError('No offer found — ask teacher to retry too.');
      }
    }
  };

  const hangUp = async () => {
    setConfirmEndOpen(false);
    const ch = callChannelRef.current;
    if (ch) {
      try { await ch.send({ type: 'broadcast', event: 'vc-hangup', payload: {} }); } catch (_) {}
      try { await ch.track({ role: isStudent ? 'student' : 'teacher', hasOffer: false, offer: null }); } catch (_) {}
    }
    doCleanup(true);
    if (onClose) onClose();
  };

  const requestHangUp = () => setConfirmEndOpen(true);
  const cancelHangUp = () => setConfirmEndOpen(false);

  const toggleMic = () => {
    const next = !micOn;
    setMicOn(next);
    const ls = localStreamRef.current;
    if (ls) ls.getAudioTracks().forEach(t => { t.enabled = next; });
  };

  const toggleCam = () => {
    const next = !camOn;
    setCamOn(next);
    const ls = localStreamRef.current;
    if (ls) ls.getVideoTracks().forEach(t => { t.enabled = next; });
    if (localVideoRef.current) localVideoRef.current.style.opacity = next ? '1' : '0.3';
  };

  // Swaps the live camera/mic track for a different device mid-call.
  // replaceTrack() on the existing sender avoids a full renegotiation
  // (no new offer/answer needed) — the remote side just starts receiving
  // frames/samples from the new source on the same connection.
  // deviceId === null means "let the browser pick a default" — used for the
  // automatic fallback when the current device disappears mid-call, which
  // shouldn't overwrite the user's saved device preference.
  const switchMediaTrack = async (kind, deviceId, persist = true) => {
    try {
      const constraints = kind === 'video'
        ? { video: deviceId ? { deviceId: { exact: deviceId } } : true }
        : { audio: deviceId ? { deviceId: { exact: deviceId } } : true };
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      const newTrack = kind === 'video' ? newStream.getVideoTracks()[0] : newStream.getAudioTracks()[0];
      if (!newTrack) return;

      newTrack.enabled = kind === 'video' ? camOn : micOn;
      newTrack.onended = () => handleDeviceUnplugged(kind);

      const sender = kind === 'video' ? videoSenderRef.current : audioSenderRef.current;
      if (sender) await sender.replaceTrack(newTrack);

      const ls = localStreamRef.current;
      if (ls) {
        const oldTracks = kind === 'video' ? ls.getVideoTracks() : ls.getAudioTracks();
        oldTracks.forEach(t => { ls.removeTrack(t); t.stop(); });
        ls.addTrack(newTrack);
      }

      if (kind === 'video') {
        setCurrentVideoDeviceId(deviceId);
        if (persist) setSavedVideoDeviceId(deviceId);
      } else {
        setCurrentAudioDeviceId(deviceId);
        if (persist) setSavedAudioDeviceId(deviceId);
      }
    } catch (e) {
      console.error(`[WebRTC] switch ${kind} device failed:`, e.message);
      setError(`Could not switch ${kind === 'video' ? 'camera' : 'microphone'}`);
    }
  };

  // Physical device unplugged / permission revoked mid-call — the track
  // just stops delivering frames/samples with no error event, only
  // `ended`. Silently fall back to whatever default device is still
  // available rather than leaving the call with a dead track.
  const handleDeviceUnplugged = (kind) => {
    console.warn(`[WebRTC] ${kind} track ended unexpectedly (device unplugged?) — falling back to default`);
    switchMediaTrack(kind, null, false);
  };

  const switchCamera = (deviceId) => switchMediaTrack('video', deviceId);
  const switchMic = (deviceId) => switchMediaTrack('audio', deviceId);

  const switchSpeaker = (deviceId) => {
    audioOutputDeviceIdRef.current = deviceId;
    setSavedAudioOutputDeviceId(deviceId);
    setCurrentAudioOutputDeviceId(deviceId);
    if (remoteVideoRef.current) applySinkId(remoteVideoRef.current);
  };

  const isFocusMode = sizeIdx === 2;
  const circleSize = SIZES[Math.min(sizeIdx, SIZES.length - 1)];

  const cycleSize = () => {
    const next = (sizeIdx + 1) % MODE_COUNT;
    setSizeIdx(next);
    const ch = callChannelRef.current;
    if (ch) {
      ch.send({ type: 'broadcast', event: 'vc-sizemode', payload: { sizeIdx: next } }).catch(() => {});
    }
  };

  return {
    error, status, channelReady,
    micOn, camOn,
    currentVideoDeviceId, currentAudioDeviceId, currentAudioOutputDeviceId,
    sizeIdx, isFocusMode, circleSize,
    iceState, showRelayBtn, speakerSupported,
    cameras, microphones, speakers,
    localVideoRef, remoteVideoRef,
    toggleMic, toggleCam,
    switchCamera, switchMic, switchSpeaker,
    cycleSize, retryRelay, joinCall,
    confirmEndOpen, requestHangUp, cancelHangUp, hangUp,
  };
}
